import { AlertCircle, MoreHorizontal, PauseCircle, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { Button } from "../../../../components/custom/custom-button";
import ReuseModal from "../../../../components/fragment/modal/ReuseModal";
import {
  deleteInfluencerContract,
  getInfluencerList,
  updateInfluencerContract,
} from "../../../../redux/influencer";
import RootState, {
  InfluencerApplicationItem,
  InfluencerContract,
  UpdateInfluencerContractPayload,
} from "../../../../redux/type";
import { ALL_ROUTES } from "../../../../routes/AllRoutes";
import { INFLUENCER_APPLICATION_ID_KEY } from "../InfluencerApplication";

const INFLUENCER_TYPE_LABELS: Record<string, string> = {
  macro_influencer: "Macro Influencer",
  micro_influencer: "Micro Influencer",
  ambassador: "Ambassador",
};

const APPLICATION_STATUS_STYLES: Record<
  string,
  { dot: string; text: string; label: string }
> = {
  pending: { dot: "bg-[#D97706]", text: "text-[#D97706]", label: "Pending" },
  approved: { dot: "bg-[#16A34A]", text: "text-[#16A34A]", label: "Approved" },
  rejected: { dot: "bg-[#DC2626]", text: "text-[#DC2626]", label: "Rejected" },
};

const CONTRACT_STATUS_STYLES: Record<string, string> = {
  active: "bg-[#DCFCE7] text-[#16A34A]",
  expired: "bg-[#FDECEC] text-[#DC2626]",
  ended: "bg-[#F4F4F5] text-[#71717A]",
};

function getInitials(name?: string | null) {
  return (name || "")
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function formatInfluencerType(type: string) {
  return INFLUENCER_TYPE_LABELS[type] || type;
}

function buildTerminatePayload(
  contract: InfluencerContract,
): UpdateInfluencerContractPayload {
  return {
    referral_code: contract.referral_code,
    target:
      contract.target != null
        ? Number(String(contract.target).replace(/,/g, ""))
        : null,
    starts_at: contract.starts_at,
    ends_at: contract.ends_at,
    qualifying_thresholds: contract.qualifying_thresholds,
    rewards: contract.rewards,
    status: "terminated",
  };
}

interface InfluencerActionsMenuProps {
  disabled?: boolean;
  onTerminate: () => void;
  onDelete: () => void;
}

function InfluencerActionsMenu({
  disabled,
  onTerminate,
  onDelete,
}: InfluencerActionsMenuProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="flex size-[32px] items-center justify-center rounded-full bg-[#F4F4F5] text-[#758089] disabled:opacity-50"
      >
        <MoreHorizontal className="size-[16px]" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-[38px] z-20 w-[190px] rounded-[8px] border border-[#E4E4E7] bg-white py-[6px] shadow-[0_4px_16px_rgba(0,0,0,0.08)]">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onTerminate();
              }}
              className="flex w-full items-center gap-[8px] px-[14px] py-[8px] text-[13px] text-[#3F3F46] hover:bg-[#F4F4F5]"
            >
              <PauseCircle className="size-[14px]" />
              Terminate Influencer
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
              className="flex w-full items-center gap-[8px] px-[14px] py-[8px] text-[13px] text-[#DC2626] hover:bg-[#FEF2F2]"
            >
              <Trash2 className="size-[14px]" />
              Delete Influencer
            </button>
          </div>
        </>
      )}
    </div>
  );
}

interface ConfirmAction {
  type: "delete" | "terminate";
  application: InfluencerApplicationItem;
}

export default function InfluencerProgram() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { influencers, loading_influencers, loading_influencer_contract_action } =
    useSelector((state: RootState) => state.influencer);
  const { user } = useSelector((state: RootState) => state.auth);
  const canManageContract = !!user?.permissions?.includes(
    "terminate_influencer_contract",
  );

  const [actioningId, setActioningId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);

  const fetchInfluencers = () => {
    dispatch(getInfluencerList() as any);
  };

  useEffect(() => {
    fetchInfluencers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const applications = influencers?.data || [];
  const isInitialLoad = loading_influencers && !influencers;

  const handleTerminate = async (application: InfluencerApplicationItem) => {
    const contract = application.influencer_contract;
    if (!contract) return;
    setActioningId(application.id);
    const result: any = await dispatch(
      updateInfluencerContract({
        id: contract.id,
        payload: buildTerminatePayload(contract),
      }) as any,
    );
    setActioningId(null);
    if (result?.payload?.data?.status) {
      setConfirmAction(null);
      fetchInfluencers();
    }
  };

  const handleDelete = async (application: InfluencerApplicationItem) => {
    const contract = application.influencer_contract;
    if (!contract) return;
    setActioningId(application.id);
    const result: any = await dispatch(
      deleteInfluencerContract(contract.id) as any,
    );
    setActioningId(null);
    if (result?.payload?.data?.status) {
      setConfirmAction(null);
      fetchInfluencers();
    }
  };

  const handleViewSubmission = (application: InfluencerApplicationItem) => {
    localStorage.setItem(INFLUENCER_APPLICATION_ID_KEY, application.id);
    navigate(ALL_ROUTES?.DASHBOARD_REFERRAL_INFLUENCER_APPLICATION);
  };

  return (
    <div className="py-[16px] space-y-[20px]">
      <div>
        <p className="text-[20px] font-bold text-[#1E1E1E]">
          Influencer Management
        </p>
        <p className="text-[14px] text-[#758089] mt-[4px]">
          Manage referral codes and reward campaigns for approved influencers
        </p>
      </div>

      <div className="bg-white border border-[#E4E4E7] rounded-[8px] p-[20px]">
        <p className="text-[16px] font-bold text-[#1E1E1E]">
          All influencers ({influencers?.meta?.total ?? applications.length})
        </p>
        <p className="text-[13px] text-[#758089] mt-[2px]">
          Basic details about each influencer application
        </p>

        <div className="mt-[16px] flex items-start gap-[10px] rounded-[8px] bg-[#FFFBEB] px-[16px] py-[12px]">
          <AlertCircle className="size-[16px] text-[#D97706] shrink-0 mt-[2px]" />
          <p className="text-[13px] text-[#92400E]">
            <span className="font-semibold">Rules:</span> All codes must be
            unique and cannot be reused. Expired codes will no longer generate
            referrals. Codes auto-expire at the contract end date
          </p>
        </div>

        <div className="mt-[16px]">
          {isInitialLoad ? (
            <div className="space-y-[16px]">
              <Skeleton height={60} />
              <Skeleton height={60} />
              <Skeleton height={60} />
            </div>
          ) : applications.length === 0 ? (
            <p className="py-[40px] text-center text-[14px] text-[#A1A1AA]">
              No influencer applications yet
            </p>
          ) : (
            applications.map((application) => {
              if (!application) return null;
              const statusStyle =
                APPLICATION_STATUS_STYLES[application.status || ""] ||
                APPLICATION_STATUS_STYLES.pending;
              const contract = application.influencer_contract;
              const contractStatusLabel = contract?.derived_status
                ? contract.derived_status.charAt(0).toUpperCase() +
                  contract.derived_status.slice(1)
                : "—";

              return (
                <div
                  key={application.id}
                  className="flex flex-wrap items-center justify-between gap-[16px] border-b border-[#F0F0F0] py-[16px] last:border-b-0"
                >
                  <div className="flex items-center gap-[12px]">
                    <span className="flex size-[40px] shrink-0 items-center justify-center rounded-full bg-[#DCFCE7] text-[14px] font-bold text-[#16A34A]">
                      {getInitials(application.full_name)}
                    </span>
                    <div>
                      <p className="text-[14px] font-bold text-[#1E1E1E]">
                        {application.full_name || "—"}
                        <span className="ml-[8px] font-normal text-[#758089]">
                          {application.email || ""}
                        </span>
                      </p>
                      <p className="text-[13px] text-[#758089] mt-[2px]">
                        {application.country?.name || "—"} ·{" "}
                        {formatInfluencerType(application.influencer_type || "")}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-[12px] shrink-0">
                    <span
                      className={`inline-flex items-center gap-[6px] text-[13px] font-medium ${statusStyle.text}`}
                    >
                      <span className={`size-[6px] rounded-full ${statusStyle.dot}`} />
                      {statusStyle.label}
                    </span>

                    {contract && (
                      <>
                        <span className="rounded-[6px] bg-[#F4F4F5] px-[10px] py-[4px] text-[12px] font-medium text-[#3F3F46]">
                          {contract.referral_code || "—"}
                        </span>
                        <span
                          className={`rounded-full px-[10px] py-[4px] text-[12px] font-medium ${
                            CONTRACT_STATUS_STYLES[contract.derived_status || ""] ||
                            "bg-[#F4F4F5] text-[#71717A]"
                          }`}
                        >
                          {contractStatusLabel}
                        </span>
                      </>
                    )}

                    <Button
                      variant="primary-outline"
                      onClick={() => handleViewSubmission(application)}
                    >
                      View Submissions
                    </Button>

                    {contract && canManageContract && (
                      <InfluencerActionsMenu
                        disabled={actioningId === application.id}
                        onTerminate={() =>
                          setConfirmAction({ type: "terminate", application })
                        }
                        onDelete={() =>
                          setConfirmAction({ type: "delete", application })
                        }
                      />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <ReuseModal
        visible={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onBtnClick={() => {
          if (!confirmAction) return;
          if (confirmAction.type === "delete") {
            handleDelete(confirmAction.application);
          } else {
            handleTerminate(confirmAction.application);
          }
        }}
        loading={loading_influencer_contract_action}
        deleteType
        btnLabel={confirmAction?.type === "delete" ? "Delete" : "Terminate"}
        title={
          confirmAction?.type === "delete"
            ? "Delete Influencer Contract"
            : "Terminate Influencer Contract"
        }
        deleteText={
          confirmAction?.type === "delete"
            ? `Are you sure you want to permanently delete ${confirmAction.application.full_name}'s contract? This action cannot be undone.`
            : `Are you sure you want to terminate ${confirmAction?.application.full_name}'s contract? They will no longer be able to generate referrals.`
        }
      />
    </div>
  );
}
